import { Chip } from '@mui/material';
import React from 'react';
import AssetImage from '../AssetImage';
import { formatHighlightDate } from './fundUpdatesAdminUtils';

const APP_COLORS = {
  text: '#113833',
  textSecondary: '#667085',
  primaryDarker: '#0e2a24',
  borderLight: '#d0d5dd',
  surfaceVariant: '#f1f4f8',
};
const APP_FONT = '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';

/** Phone-style preview card mimicking the exact Fund Highlights card as rendered in the app */
export default function HighlightCardPreview({
  date,
  fundName,
  title,
  summary,
  imageUrl,
  isInferred,
}: {
  date: string | null;
  fundName: string;
  title: string;
  summary: string;
  imageUrl: string | null;
  isInferred: boolean;
}) {
  const formattedDate = date ? formatHighlightDate(date) : null;
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        style={{
          width: 320,
          borderWidth: 1,
          border: `1px solid ${APP_COLORS.borderLight}`,
          borderRadius: 12,
          backgroundColor: APP_COLORS.surfaceVariant,
          padding: 12,
          fontFamily: APP_FONT,
          boxSizing: 'border-box',
        }}
      >
        {imageUrl && (
          <AssetImage
            url={imageUrl}
            sx={{
              width: '100%',
              height: 140,
              objectFit: 'cover',
              display: 'block',
              borderRadius: '8px',
              marginBottom: '8px',
            }}
          />
        )}
        {formattedDate && (
          <div
            style={{
              fontSize: 11,
              lineHeight: '16px',
              letterSpacing: 0.4,
              fontFamily: APP_FONT,
              color: APP_COLORS.textSecondary,
              textTransform: 'uppercase',
              marginBottom: 4,
              fontWeight: 500,
            }}
          >
            {formattedDate}
          </div>
        )}
        {fundName && (
          <div
            style={{
              fontSize: 14,
              lineHeight: '20px',
              letterSpacing: 0.1,
              fontFamily: APP_FONT,
              color: APP_COLORS.text,
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            {fundName}
          </div>
        )}
        {title && (
          <div
            style={
              {
                fontSize: 14,
                lineHeight: '20px',
                letterSpacing: 0.25,
                fontFamily: APP_FONT,
                color: APP_COLORS.text,
                fontWeight: 600,
                marginBottom: 4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              } as React.CSSProperties
            }
          >
            {title}
          </div>
        )}
        {summary && (
          <div
            style={
              {
                fontSize: 14,
                lineHeight: '22px',
                letterSpacing: 0.25,
                fontFamily: APP_FONT,
                color: APP_COLORS.textSecondary,
                marginBottom: 6,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              } as React.CSSProperties
            }
          >
            {summary}
          </div>
        )}
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              fontSize: 12,
              lineHeight: '16px',
              letterSpacing: 0.5,
              fontFamily: APP_FONT,
              color: APP_COLORS.primaryDarker,
              fontWeight: 600,
            }}
          >
            Open update
          </span>
          <span style={{ fontSize: 14, color: APP_COLORS.primaryDarker, lineHeight: 1 }}>→</span>
        </div>
      </div>
      {isInferred && (
        <Chip
          label="Auto-inferred"
          size="small"
          variant="outlined"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            fontSize: '0.6rem',
            height: 18,
            bgcolor: APP_COLORS.surfaceVariant,
          }}
        />
      )}
    </div>
  );
}
